import { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

const SAVE_REORDER_DEBOUNCE = 2000;

const orderByIndex = (a, b) => a.index - b.index;

const arraysAreEqual = (arr1, arr2) => {
  if (!arr1 || !arr2 || arr1.length !== arr2.length) {
    return false;
  }

  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) {
      return false;
    }
  }

  return true;
};

const SaveSidebarOrder = ({ payload, handleReorder }) => {
  const orderRef = useRef(null);
  const mountedRef = useRef(true);

  const formatPayload = payload => {
    return payload.sort(orderByIndex).map(({ id }) => id);
  };

  const checkIfPayloadHasBeenReordered = useCallback(payload => {
    const formatted = formatPayload(payload);
    return !arraysAreEqual(formatted, orderRef.current);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * Initialise ref with first value of imports order.
   */
  useEffect(() => {
    if (!orderRef.current && payload?.length > 0) {
      orderRef.current = formatPayload(payload);
    }
  }, [payload]);

  /**
   * Everytime the order of the payload changes, start a timer to save the new
   * order and debounce any future changes.
   */
  useEffect(() => {
    let timeout;

    if (
      mountedRef.current &&
      payload?.length > 0 &&
      orderRef.current &&
      checkIfPayloadHasBeenReordered(payload)
    ) {
      timeout = setTimeout(() => {
        const newOrder = formatPayload(payload);
        orderRef.current = newOrder;
        handleReorder(newOrder);
      }, SAVE_REORDER_DEBOUNCE);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [payload, checkIfPayloadHasBeenReordered, handleReorder]);

  return null;
};

SaveSidebarOrder.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  payload: PropTypes.any.isRequired,
  handleReorder: PropTypes.func.isRequired
};

export default SaveSidebarOrder;
export { orderByIndex };
